import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CalendarBlankIcon,
  CalendarIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CopyIcon,
  ListIcon,
  RowsPlusTopIcon,
  UserIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import {
  fetchBookings,
  invite,
  togglePrepaid,
  type BookingSummary,
  type InvitePackage,
} from '@/features/bookings/api';
import {
  cancelBooking,
  fetchCalendar,
  patchBooking,
  type CalendarBooking,
} from '@/features/bookings/calendar';
import { fetchProperties } from '@/features/properties/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  SkeletonRows,
  StatusChip,
  Switch,
  cn,
  fmtDate,
  fmtMoney,
  type Tone,
} from '@smena/ui';

const INVITE_TONE: Record<BookingSummary['inviteStatus'], { tone: Tone; label: string }> = {
  not_invited: { tone: 'muted', label: 'Не приглашён' },
  invited: { tone: 'accent', label: 'Приглашён' },
  joined: { tone: 'amber', label: 'Открыл чат' },
  active: { tone: 'green', label: 'Активен' },
};

// 'pending' - заявка из RealtyCalendar (их «Заявка» против «Брони»). В БД инжест пишет
// именно его (booking-ingest.repo, миграция 0032), а словарь знал только 'request' и красил
// заявку в серый «неизвестный статус» - ровно наоборот тому, что она требует внимания.
const STATUS_TONE: Record<string, Tone> = {
  confirmed: 'accent',
  prepaid: 'green',
  staying: 'green',
  checked_out: 'muted',
  cancelled: 'red',
  draft: 'amber',
  request: 'amber',
  pending: 'amber',
};

export function BookingsPage() {
  const [params, setParams] = useSearchParams();
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  // При /bookings/:id принудительно list-view (карточка справа). Явный ?view= перекрывает.
  const explicitView = params.get('view');
  const view: 'calendar' | 'list' = routeId ? 'list' : explicitView === 'list' ? 'list' : 'calendar';

  const setView = (v: 'calendar' | 'list') => {
    const p = new URLSearchParams(params);
    p.set('view', v);
    if (routeId) navigate({ pathname: '/bookings', search: `?${p.toString()}` }, { replace: true });
    else setParams(p, { replace: true });
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <div>
          <PageHeader
            icon={<CalendarIcon weight="bold" />}
            title="Брони"
            description="Заезды и выезды по всем квартирам. Брони приходят из ваших источников сами - вручную заводить не нужно."
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setView('calendar')}
            className={cn(
              'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
              view === 'calendar' ? 'border-accent bg-accent/20 text-accent-fg' : 'border-line text-muted hover:text-text',
            )}
          >
            <RowsPlusTopIcon weight="bold" className="mr-1 inline size-3.5" /> Календарь
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            className={cn(
              'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
              view === 'list' ? 'border-accent bg-accent/20 text-accent-fg' : 'border-line text-muted hover:text-text',
            )}
          >
            <ListIcon weight="bold" className="mr-1 inline size-3.5" /> Список
          </button>
        </div>
      </div>

      {view === 'calendar' ? <CalendarView /> : <ListView />}
    </div>
  );
}

// ================================================================== Calendar

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);
}
function addDays(iso: string, delta: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}
function dateRange(from: string, to: string): string[] {
  const arr: string[] = [];
  const n = daysBetween(from, to);
  for (let i = 0; i < n; i++) arr.push(addDays(from, i));
  return arr;
}
function isWeekend(iso: string): boolean {
  const d = new Date(iso + 'T00:00:00Z').getUTCDay();
  return d === 0 || d === 6;
}

const WINDOW_DAYS = 28;
const DAY_COL_PX = 40;

interface HoverState {
  b: CalendarBooking;
  x: number;
  y: number;
}

function CalendarView() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState<string>(today);
  const to = addDays(from, WINDOW_DAYS);
  const days = useMemo(() => dateRange(from, to), [from, to]);
  const navigate = useNavigate();

  const calQ = useQuery({
    queryKey: ['calendar', from, to],
    queryFn: () => fetchCalendar(from, to),
    refetchInterval: 15_000,
  });

  const [hover, setHover] = useState<HoverState | null>(null);

  return (
    <Card className="min-h-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <Button variant="ghost" size="sm" onClick={() => setFrom(addDays(from, -7))}>
          <CaretLeftIcon weight="bold" /> Неделя
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setFrom(today)}>
          Сегодня
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setFrom(addDays(from, +7))}>
          Неделя <CaretRightIcon weight="bold" />
        </Button>
        <div className="ml-3 text-xs text-muted">
          {fmtDate(from)} → {fmtDate(to)} · {days.length} дн
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {calQ.isLoading ? (
          <SkeletonRows rows={4} />
        ) : !calQ.data ? (
          <div className="p-6 text-center text-sm text-muted">Пока пусто.</div>
        ) : calQ.data.properties.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted">
            Сначала добавьте квартиру в разделе «Объекты».
          </div>
        ) : (
          <div style={{ minWidth: 220 + days.length * DAY_COL_PX }}>
            {/* Заголовок дат */}
            <div className="sticky top-0 z-10 flex border-b border-line bg-panel">
              <div
                className="sticky left-0 z-20 shrink-0 border-r border-line bg-panel px-3 py-2 text-xs uppercase tracking-wider text-muted"
                style={{ width: 220 }}
              >
                Объект
              </div>
              {days.map((d) => (
                <div
                  key={d}
                  className={cn(
                    'shrink-0 border-r border-line/50 py-2 text-center text-[10px]',
                    isWeekend(d) && 'bg-panel2/40',
                    d === today && 'bg-accent/15 text-accent-fg',
                  )}
                  style={{ width: DAY_COL_PX }}
                >
                  <div className="font-semibold">{new Date(d + 'T00:00:00Z').getUTCDate()}</div>
                  <div className="text-muted">
                    {new Date(d + 'T00:00:00Z').toLocaleDateString('ru-RU', {
                      weekday: 'short',
                      timeZone: 'UTC',
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Строки объектов */}
            {calQ.data.properties.map((prop) => {
              const propBookings = calQ.data!.bookings.filter((b) => b.propertyId === prop.id);
              return (
                <div key={prop.id} className="relative flex border-b border-line/40">
                  <div
                    className="sticky left-0 z-10 shrink-0 border-r border-line bg-panel px-3 py-3 text-sm"
                    style={{ width: 220 }}
                  >
                    <div className="truncate font-medium">{prop.title}</div>
                    <div className="text-[11px] text-muted">{prop.city ?? '-'}</div>
                  </div>
                  <div className="relative" style={{ width: days.length * DAY_COL_PX, height: 64 }}>
                    {days.map((d) => (
                      <div
                        key={d}
                        className={cn(
                          'absolute inset-y-0 border-r border-line/30',
                          isWeekend(d) && 'bg-panel2/20',
                          d === today && 'bg-accent/10',
                        )}
                        style={{ left: days.indexOf(d) * DAY_COL_PX, width: DAY_COL_PX }}
                      />
                    ))}
                    {propBookings.map((b) => (
                      <BookingBar
                        key={b.id}
                        b={b}
                        from={from}
                        totalDays={days.length}
                        highlighted={hover?.b.id === b.id}
                        onClick={() => navigate(`/bookings/${b.id}`)}
                        onHover={(x, y) => setHover({ b, x, y })}
                        onLeave={() => setHover(null)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {hover ? <HoverCard hover={hover} /> : null}
    </Card>
  );
}

function HoverCard({ hover }: { hover: HoverState }) {
  const { b, x, y } = hover;
  // Позиционируем над курсором (y-12 = смещение вверх, x+16 = справа от полосы).
  // Клипуем к viewport - если справа не влезает, показываем слева от курсора.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const W = 260;
  const left = x + 16 + W > vw ? Math.max(8, x - W - 16) : x + 16;
  const top = Math.max(8, y - 12);
  return (
    <div
      className="pointer-events-none fixed z-50 rounded-md border border-line bg-panel px-3 py-2.5 text-xs shadow-lg"
      style={{ left, top, width: W }}
    >
      <div className="mb-1 text-sm font-semibold">{b.guestName}</div>
      <div className="mb-2 flex flex-wrap gap-1">
        <StatusChip tone={b.status === 'confirmed' ? 'accent' : 'muted'}>{b.status}</StatusChip>
        {b.prepaid ? <StatusChip tone="green">Оплата</StatusChip> : null}
        <StatusChip tone="muted">{b.source}</StatusChip>
      </div>
      <div className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-0.5 text-muted">
        <span>Заезд:</span>
        <span className="text-text">{fmtDate(b.checkin)}</span>
        <span>Выезд:</span>
        <span className="text-text">{fmtDate(b.checkout)}</span>
        <span>Ночей:</span>
        <span className="text-text">{Math.max(1, Math.round((Date.parse(b.checkout) - Date.parse(b.checkin)) / 86_400_000))}</span>
        <span>Сумма:</span>
        <span className="text-text tabular-nums">{fmtMoney(b.amount)}</span>
      </div>
      <div className="mt-2 text-[10px] text-muted">Нажмите, чтобы открыть бронь</div>
    </div>
  );
}

function BookingBar({
  b,
  from,
  totalDays,
  highlighted,
  onClick,
  onHover,
  onLeave,
}: {
  b: CalendarBooking;
  from: string;
  totalDays: number;
  highlighted: boolean;
  onClick: () => void;
  onHover: (x: number, y: number) => void;
  onLeave: () => void;
}) {
  const to = addDays(from, totalDays);
  // Клип интервала броней к окну.
  const clipFromDays = Math.max(0, daysBetween(from, b.checkin));
  const clipToDays = Math.min(totalDays, daysBetween(from, b.checkout));
  if (clipToDays <= clipFromDays) return null;

  // Сдвиг полдня с обеих сторон: checkin с середины дня заезда, checkout до середины дня выезда.
  const leftPx = clipFromDays * DAY_COL_PX + (b.checkin >= from ? DAY_COL_PX / 2 : 0);
  const rightPx = clipToDays * DAY_COL_PX - (b.checkout <= to ? DAY_COL_PX / 2 : 0);
  const widthPx = Math.max(rightPx - leftPx, DAY_COL_PX / 3);

  // Полосы календаря - сплошная заливка статусным цветом: под белым текстом это 4.5:1+,
  // тогда как полупрозрачная версия поверх светлого фона роняет контраст до ~3:1.
  const tone =
    b.status === 'confirmed' && b.prepaid
      ? 'bg-green border-green'
      : b.status === 'confirmed'
        ? 'bg-accent border-accent'
        : b.status === 'request' || b.status === 'pending' || b.status === 'draft'
          ? 'bg-amber border-amber'
          : 'bg-panel2 border-line text-text';

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={(e) => onHover(e.clientX, e.clientY)}
      onMouseMove={(e) => onHover(e.clientX, e.clientY)}
      onMouseLeave={onLeave}
      className={cn(
        'absolute top-2 h-12 rounded-md border px-2 text-left text-[11px] leading-tight text-white shadow transition-transform hover:scale-[1.015]',
        tone,
        highlighted && 'ring-2 ring-ink',
      )}
      style={{ left: leftPx, width: widthPx }}
      aria-label={`Бронь ${b.guestName}, ${fmtDate(b.checkin)} - ${fmtDate(b.checkout)}`}
    >
      <div className="truncate font-semibold">{b.guestName}</div>
      <div className="truncate opacity-90">
        {fmtDate(b.checkin)} - {fmtDate(b.checkout)}
      </div>
    </button>
  );
}

// ================================================================== List (порт Ф0.6)

function ListView() {
  const bookingsQ = useQuery({
    queryKey: ['bookings'],
    queryFn: fetchBookings,
    refetchInterval: 5_000,
  });
  // Объект брони: bookings отдаёт только propertyId, а оператору нужно название.
  // Без него список выглядел так, будто бронь ни к какой квартире не привязана.
  const propsQ = useQuery({ queryKey: ['properties'], queryFn: fetchProperties });
  const propertyById = useMemo(() => {
    const m = new Map<string, { title: string; city: string | null }>();
    for (const p of propsQ.data ?? []) m.set(p.id, { title: p.title, city: p.city });
    return m;
  }, [propsQ.data]);
  const { id: routeId } = useParams<{ id?: string }>();
  const [selectedId, setSelectedId] = useState<string | null>(routeId ?? null);
  // При смене :id в URL - подхватываем автоматом.
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);
  const [invitePkg, setInvitePkg] = useState<InvitePackage | null>(null);
  const selected = bookingsQ.data?.find((b) => b.id === selectedId) ?? null;

  const inviteMut = useMutation({
    mutationFn: invite,
    onSuccess: (pkg) => {
      setInvitePkg(pkg);
      toast.success('Приглашение сформировано');
    },
    onError: (err: Error) => toast.error(`Ошибка: ${err.message}`),
  });

  const qc = useQueryClient();
  const togglePrepaidMut = useMutation({
    mutationFn: ({ id, prepaid }: { id: string; prepaid: boolean }) => togglePrepaid(id, prepaid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      toast.success('Предоплата обновлена');
    },
  });

  const cancelMut = useMutation({
    mutationFn: cancelBooking,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      toast.success('Бронь отменена');
    },
  });

  return (
    // На узком экране это была сетка из двух жёстких колонок: правая забирала 320px,
    // левая схлопывалась до нечитаемой полосы, и выбрать бронь было нечем.
    // Поведение как в «Диалогах»: на мобильном одна колонка, выбранная бронь заменяет список.
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
      <Card className={cn('min-h-0 overflow-auto', selectedId && 'hidden md:block')}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-panel text-left text-xs uppercase tracking-wider text-muted">
            <tr className="border-b border-line">
              <th className="px-4 py-3 font-medium">Гость</th>
              {/* На мобильном объект уезжает под имя гостя: отдельной колонкой таблица
                  вылезала за экран и даты со статусом приходилось доскроливать вбок. */}
              <th className="hidden px-4 py-3 font-medium md:table-cell">Объект</th>
              {/* Ночей отдельной колонкой не держим: с колонкой «Объект» таблица переставала
                  влезать в левую панель и обрезала «Статус». Число ночей ушло под даты. */}
              <th className="px-4 py-3 font-medium">Даты</th>
              <th className="px-4 py-3 font-medium">Сумма</th>
              <th className="px-4 py-3 font-medium">Статус</th>
            </tr>
          </thead>
          <tbody>
            {bookingsQ.isLoading ? (
              <tr>
                <td colSpan={5} className="p-0">
                  <SkeletonRows rows={4} />
                </td>
              </tr>
            ) : bookingsQ.data && bookingsQ.data.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted">
                  Броней пока нет. Они появятся сами, как только подключите источник в «Интеграциях».
                </td>
              </tr>
            ) : (
              bookingsQ.data?.map((b) => (
                <tr
                  key={b.id}
                  onClick={() => {
                    setSelectedId(b.id);
                    setInvitePkg(null);
                  }}
                  className={cn(
                    'cursor-pointer border-b border-line/50 transition-colors hover:bg-panel2',
                    selectedId === b.id && 'bg-panel2',
                  )}
                >
                  {/* max-w на мобильном обязателен: без него truncate внизу не работает
                      (ячейка таблицы растягивается под контент) и длинное название объекта
                      уносит таблицу за экран. */}
                  <td className="max-w-[52vw] px-4 py-3 md:max-w-none">
                    <div className="flex items-center gap-2">
                      <UserIcon weight="regular" className="size-4 shrink-0 text-muted" />
                      <span className="whitespace-nowrap font-medium">{b.guestName}</span>
                    </div>
                    {b.guestPhone ? <div className="mt-0.5 text-xs text-muted">{b.guestPhone}</div> : null}
                    <div className="mt-1 truncate text-xs text-muted md:hidden">
                      {propertyById.get(b.propertyId)?.title ?? (propsQ.isLoading ? '…' : 'объект не найден')}
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    <PropertyCell property={propertyById.get(b.propertyId)} loading={propsQ.isLoading} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>{fmtDate(b.checkin)}</div>
                    <div className="text-xs text-muted">
                      → {fmtDate(b.checkout)} · {b.nights} ноч.
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{fmtMoney(b.amount)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <StatusChip tone={STATUS_TONE[b.status] ?? 'muted'}>{b.status}</StatusChip>
                      <StatusChip tone={INVITE_TONE[b.inviteStatus].tone}>
                        {INVITE_TONE[b.inviteStatus].label}
                      </StatusChip>
                      {b.prepaid ? <StatusChip tone="green">Оплата</StatusChip> : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <Card className={cn('min-h-0 overflow-auto', !selected && 'hidden md:block')}>
        {!selected ? (
          <CardContent className="pt-6 text-center text-sm text-muted">
            Выберите бронь слева
          </CardContent>
        ) : (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="-ml-1 shrink-0 rounded-md p-1 text-muted hover:bg-panel2 hover:text-text md:hidden"
                  aria-label="Назад к списку броней"
                >
                  <CaretLeftIcon weight="bold" className="size-5" />
                </button>
                <CalendarBlankIcon weight="bold" className="size-4 text-accent-fg" />
                {selected.guestName}
              </CardTitle>
              <div className="text-xs text-muted">
                {propertyById.get(selected.propertyId)?.title ?? 'объект не найден'}
              </div>
              <div className="text-xs text-muted">
                {fmtDate(selected.checkin)} → {fmtDate(selected.checkout)} · {selected.nights} ноч. · {fmtMoney(selected.amount)}
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <BookingEditor booking={selected} />

              <label className="flex cursor-pointer items-center justify-between gap-4 rounded-md border border-line px-3 py-2.5">
                <span className="min-w-0">
                  <span className="block font-semibold">Предоплата получена</span>
                  <span className="mt-0.5 block text-xs leading-snug text-muted">
                    Без неё Smena не отправит гостю код замка
                  </span>
                </span>
                <Switch
                  checked={!!selected.prepaid}
                  onChange={(v) => togglePrepaidMut.mutate({ id: selected.id, prepaid: v })}
                  aria-label="Предоплата получена"
                />
              </label>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => inviteMut.mutate(selected.id)}
                  disabled={inviteMut.isPending}
                >
                  {inviteMut.isPending ? 'Формируем…' : 'Сгенерировать приглашение'}
                </Button>
                {selected.status !== 'cancelled' ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Отменить бронь «${selected.guestName}»?`)) {
                        cancelMut.mutate(selected.id);
                      }
                    }}
                    disabled={cancelMut.isPending}
                    className="text-destructive"
                  >
                    <XCircleIcon weight="bold" /> Отменить бронь
                  </Button>
                ) : null}
              </div>

              {invitePkg ? <InviteBlock pkg={invitePkg} /> : null}
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Объект брони в списке. Бронь без объекта в БД невозможна (property_id NOT NULL), поэтому
 * пустое место здесь означает рассинхрон кэша, а не «бронь ни к чему не привязана» - так и
 * пишем, чтобы оператор не искал несуществующую проблему.
 */
function PropertyCell({
  property,
  loading,
}: {
  property?: { title: string; city: string | null };
  loading: boolean;
}) {
  if (property) {
    return (
      <>
        <div className="max-w-[18ch] truncate font-medium" title={property.title}>
          {property.title}
        </div>
        {property.city ? <div className="mt-0.5 text-xs text-muted">{property.city}</div> : null}
      </>
    );
  }
  return <span className="text-xs text-muted">{loading ? '…' : 'объект не найден'}</span>;
}

function BookingEditor({ booking }: { booking: BookingSummary }) {
  const [checkin, setCheckin] = useState(booking.checkin);
  const [checkout, setCheckout] = useState(booking.checkout);
  const [amount, setAmount] = useState<number>(booking.amount);
  const [dirty, setDirty] = useState(false);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: () => patchBooking(booking.id, { checkin, checkout, amount }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      setDirty(false);
      toast.success('Изменения сохранены');
    },
    onError: (err: Error & { status?: number }) => {
      if (String(err.message).includes('конфликт дат')) {
        toast.error('Конфликт дат: пересечение с другой бронью того же объекта');
      } else {
        toast.error(`Ошибка: ${err.message}`);
      }
    },
  });

  return (
    <div className="grid grid-cols-2 gap-2 rounded-md border border-line bg-panel2/30 p-3">
      <div className="flex flex-col gap-1">
        <Label>Заезд</Label>
        <Input
          type="date"
          value={checkin}
          onChange={(e) => {
            setCheckin(e.target.value);
            setDirty(true);
          }}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label>Выезд</Label>
        <Input
          type="date"
          value={checkout}
          onChange={(e) => {
            setCheckout(e.target.value);
            setDirty(true);
          }}
        />
      </div>
      <div className="col-span-2 flex flex-col gap-1">
        <Label>Сумма, ₽</Label>
        <Input
          type="number"
          value={amount}
          onChange={(e) => {
            setAmount(Number(e.target.value));
            setDirty(true);
          }}
        />
      </div>
      <Button
        size="sm"
        onClick={() => save.mutate()}
        disabled={!dirty || save.isPending}
        className="col-span-2"
      >
        {save.isPending ? 'Сохраняем…' : dirty ? 'Сохранить правки' : 'Сохранено'}
      </Button>
    </div>
  );
}

function InviteBlock({ pkg }: { pkg: InvitePackage }) {
  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Не удалось скопировать - выделите вручную');
    }
  }

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-3">
      {pkg.accessCode ? (
        <div className="flex items-center gap-3 rounded-md border border-line bg-panel2 p-3">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-muted">Код входа для гостя</div>
            <div className="mt-0.5 font-mono text-2xl font-bold tracking-[0.4em]">{pkg.accessCode}</div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => copy(pkg.accessCode!, 'Код скопирован')}>
            <CopyIcon weight="bold" /> Код
          </Button>
        </div>
      ) : (
        <div className="text-xs text-muted">Код входа не нужен: ссылка открывается сразу.</div>
      )}

      {pkg.channels.map((ch, idx) => (
        <div key={idx} className={cn('rounded-md border p-3', ch.enabled ? 'border-line' : 'border-line/60 opacity-70')}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{ch.label}</span>
            <span className="shrink-0 text-[11px] text-muted">
              {ch.enabled ? 'готово к отправке' : 'не подключено'}
            </span>
          </div>
          {/* Причина - отдельной строкой: это предложение, а не статус, и в строку с меткой
              оно не влезает. */}
          {!ch.enabled && ch.note ? (
            <div className="mb-2 text-[11px] leading-snug text-muted">{ch.note}</div>
          ) : null}
          {/* У части способов приглашения нет в принципе (канал не подключён) - бэк отдаёт
              пустой text с причиной. Пустая textarea и кнопка «Текст» тут были бы обманом. */}
          {ch.text ? (
            <>
              <textarea
                readOnly
                value={ch.text}
                rows={4}
                className="w-full resize-y rounded border border-line bg-input px-2 py-1.5 font-mono text-xs text-text"
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => copy(ch.text, 'Текст скопирован')}>
                  <CopyIcon weight="bold" /> Текст
                </Button>
                {ch.url ? (
                  <Button size="sm" variant="ghost" onClick={() => copy(ch.url!, 'Ссылка скопирована')}>
                    Только ссылку
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}
